import { observer } from 'mobx-react-lite'
import { type UIEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router'
import classNames from 'classnames'
import { type IVirtualListRef, Scrollbar, SpinnerLoader } from 'shared/ui'
import useIsTabActive from 'shared/hooks/useIsTabActive'
import { getAriaLabel } from 'shared/lib'
import { ScrollToBottomBtn } from 'shared/ui/ScrollToBottomBtn/ScrollToBottomBtn'
import { type Activity } from 'entities/Activity/model/Activity'
import { type Message } from 'entities/Message/model/Message'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { MessageCard } from 'widgets/MessageCard'
import { DayContent } from 'widgets/DayContent/DayContent'
import { useEventMessageTranscriptCompleted } from 'widgets/ConversationMessages/events/EventMessageTranscriptCompleted'
import { useConversationMessagesContext } from 'widgets/ConversationMessages/context'
import { useEventMessageUpdatedNew } from 'widgets/ConversationMessages/events/EventMessageUpdatedNew'
import { useEventMessageReceivedNew } from 'widgets/ConversationMessages/events/EventMessageReceivedNew'
import { useEventVoicemailReceived } from 'widgets/ConversationMessages/events/EventVoicemailReceived'
import { useEventConversationActivityCreated } from 'widgets/ConversationMessages/events/EventConversationActivityCreated'
import { useEventTyping } from 'widgets/ConversationMessages/events/EventTyping'
import { useEventViewing } from 'widgets/ConversationMessages/events/EventViewing'
import { EmptyState } from 'widgets/ConversationMessages/ui/ConversationContent/ui/EmptyState/EmptyState'
import { useEventMessageTranscriptStarted } from 'widgets/ConversationMessages/events/EventMessageTranscriptStarted'
import { useEventMessageTranscriptFailed } from 'widgets/ConversationMessages/events/EventMessageTranscriptFailed'
import {
  ESTIMATE_SIZE,
  HIDE_AVATARS_WIDTH,
  OVERSCAN,
  PADDING_END,
  SCROLL_END_THRESHOLD,
  SENDING_MESSAGES_KEY,
  SHOW_ARROW_BOTTOM,
  START_REACHED_THRESHOLD,
} from './constants/constants'
import styles from './styles.module.scss'

export const ConversationContentVirtualize = observer(() => {
  const { conversationStore } = useEntitiesContext()
  const store = useConversationMessagesContext()
  const [searchParams] = useSearchParams()
  const {
    scrollBottomTrigger,
    hasPrevious,
    loading,
    loadingPrevious,
    sendingMessages,
    callContentStore,
    setScrollBottomTrigger,
    loadPreviousMessages,
    setIsActiveTab,
    setHideAvatars,
    scheduledItems,
    ordinaryItems,
    isUnread,
    setIsBottomFixed,
    mediaLoadedTrigger,
    messageId: storeMessageId,
    loadingOverlay,
  } = store
  const listRef = useRef<IVirtualListRef>(null)
  const scrollHeightRef = useRef(0)
  const sendingMessagesCountRef = useRef(0)
  const [scrollView, setScrollView] = useState<HTMLElement | null>(null)
  const [visibleArrowBottom, setVisibleArrowBottom] = useState(false)
  const [hasScrolledToMessage, setHasScrolledToMessage] = useState(false)
  const [scrollTarget, setScrollTarget] = useState<{
    conversationId: number
    messageId: string
  } | null>(null)
  const requestedMessageId = storeMessageId || searchParams.get('messageId')
  const messageId =
    scrollTarget?.conversationId === store.conversationId ? scrollTarget.messageId : null

  const scrollToBottom = () => listRef.current?.scrollToEnd()

  const scrollToMessage = (targetMessageId: string) => {
    const index = [...ordinaryItems, ...scheduledItems].findIndex(
      (item) => String(item.id) === targetMessageId
    )
    const list = listRef.current

    if (index < 0 || !list) return

    setIsBottomFixed(false)
    list.scrollToIndex(index, { align: 'center' })
    setHasScrolledToMessage(true)
  }

  useEffect(() => {
    if (!requestedMessageId) {
      setScrollTarget((target) =>
        target && target.conversationId !== store.conversationId ? null : target
      )

      return
    }

    setScrollTarget({ conversationId: store.conversationId, messageId: String(requestedMessageId) })
    setHasScrolledToMessage(false)
  }, [requestedMessageId, store.conversationId])

  useEffect(() => {
    if (sendingMessages.length > sendingMessagesCountRef.current) setScrollTarget(null)

    sendingMessagesCountRef.current = sendingMessages.length
  }, [sendingMessages.length])

  useEffect(() => {
    if (!scrollBottomTrigger || messageId) return

    scrollToBottom()
  }, [scrollBottomTrigger])

  useEffect(() => {
    if (!messageId || loading || hasScrolledToMessage) return

    scrollToMessage(messageId)
  }, [
    mediaLoadedTrigger,
    messageId,
    loading,
    hasScrolledToMessage,
    ordinaryItems.length,
    scheduledItems.length,
  ])

  useEventMessageUpdatedNew(store)
  useEventMessageReceivedNew(store)
  useEventVoicemailReceived(store)
  useEventMessageTranscriptCompleted(store)
  useEventMessageTranscriptStarted(store)
  useEventMessageTranscriptFailed(store)
  useEventConversationActivityCreated(store)
  useEventTyping(store)
  useEventViewing(store)

  const isActiveTab = useIsTabActive()

  useEffect(() => {
    setIsActiveTab(isActiveTab)
  }, [isActiveTab])

  const handleLoadMore = () => {
    if (!hasPrevious) return

    return loadPreviousMessages()
  }

  const handleSetVisibleArrowBottom = () => {
    if (!scrollView) return

    const scrollFromBottom =
      scrollView.scrollHeight - (scrollView.clientHeight + scrollView.scrollTop)

    setVisibleArrowBottom(scrollFromBottom > SHOW_ARROW_BOTTOM)
  }

  const onScroll = (e: UIEvent) => {
    if (!(e.target instanceof Element)) return

    const { scrollHeight, clientHeight, scrollTop } = e.target
    const scrollFromBottom = scrollHeight - (clientHeight + scrollTop)

    setVisibleArrowBottom(scrollFromBottom > SHOW_ARROW_BOTTOM)

    if (scrollHeightRef.current && scrollHeightRef.current === scrollHeight) {
      setIsBottomFixed(scrollFromBottom === 0)
    }
    scrollHeightRef.current = scrollHeight
  }

  const onClickScrollToBottom = async () => {
    setScrollTarget(null)

    await store.loadMessagesAllNext()

    scrollToBottom()

    if (!store.isRead) {
      conversationStore.onRead(store.conversationId)
    }
  }

  useEffect(() => {
    if (!scrollView) return

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setHideAvatars(
          (entry.borderBoxSize?.[0]?.inlineSize ?? scrollView.clientWidth) <= HIDE_AVATARS_WIDTH
        )
      }
      handleSetVisibleArrowBottom()
    })

    resizeObserver.observe(scrollView)

    return () => resizeObserver.disconnect()
  }, [scrollView])

  useEffect(() => {
    handleSetVisibleArrowBottom()
  }, [ordinaryItems.length, scheduledItems.length, sendingMessages.length, mediaLoadedTrigger])

  const renderItems = useCallback(
    (items: (Message | Activity)[], isScheduled?: boolean) =>
      items.map((item, index) => (
        <DayContent
          key={item.id}
          isHideDay={!!index && item.day === items[index - 1]?.day}
          day={item.day}
          items={[item]}
          isScheduled={isScheduled}
          conversationMessagesStore={store}
          reserveMediaSize
        />
      )),
    [store]
  )

  const ordinaryContent = useMemo(() => renderItems(ordinaryItems), [renderItems, ordinaryItems])
  const scheduledContent = useMemo(
    () => renderItems(scheduledItems, true),
    [renderItems, scheduledItems]
  )

  const selectedSearch = store.selectedSearch
  const sendingContent = useMemo(
    () =>
      sendingMessages.length ? (
        <div
          key={SENDING_MESSAGES_KEY}
          className={styles.warpSending}
          aria-label={getAriaLabel('Sending Message', 'wrap')}
        >
          {sendingMessages.map((message) => (
            <MessageCard
              key={message.client_id}
              message={message}
              selectedSearch={selectedSearch}
              setScrollBottomTrigger={setScrollBottomTrigger}
              callContentStore={callContentStore}
              conversationMessagesStore={store}
              reserveMediaSize
            />
          ))}
        </div>
      ) : null,
    [sendingMessages, selectedSearch, setScrollBottomTrigger, callContentStore, store]
  )

  const virtualContent = useMemo(() => {
    const content = [...ordinaryContent, ...scheduledContent]

    if (sendingContent) content.push(sendingContent)

    return content
  }, [ordinaryContent, scheduledContent, sendingContent])

  const loadingSpinner = hasPrevious ? (
    <SpinnerLoader
      ariaLabel={'ConversationContent'}
      size={16}
      className={classNames(styles.spinner, {
        [styles.spinnerIdle]: !loading && !loadingPrevious,
      })}
    />
  ) : null

  return (
    <div className={styles.content}>
      {loadingOverlay && (
        <div className={styles.spinnerOverlay}>
          <SpinnerLoader ariaLabel={'ConversationContentAll'} size={32} />
        </div>
      )}

      <ScrollToBottomBtn
        open={visibleArrowBottom && !loading}
        onClick={onClickScrollToBottom}
        primary={Boolean(isUnread)}
      />

      <Scrollbar
        virtualized
        virtualListRef={listRef}
        onViewRef={setScrollView}
        viewClassName={styles.wrapVirtualized}
        trackVerticalClassName={styles.scrollTrackVirtualized}
        contentWrapperClassName={styles.messagesBottomAlign}
        contentClassName={styles.messagesColumn}
        estimateSize={ESTIMATE_SIZE}
        overscan={OVERSCAN}
        paddingEnd={PADDING_END}
        anchorTo={messageId ? undefined : 'end'}
        followOnAppend
        directDomUpdates
        flushSyncOnScroll={false}
        useAnimationFrameWithResizeObserver
        scrollEndThreshold={SCROLL_END_THRESHOLD}
        startReachedThreshold={START_REACHED_THRESHOLD}
        header={loadingSpinner}
        onScroll={onScroll}
        onStartReached={handleLoadMore}
      >
        {virtualContent}
      </Scrollbar>
      <div className={styles.emptyStateOverlay}>
        <EmptyState />
      </div>
    </div>
  )
})
